import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Home,
  FileText,
  User,
  Briefcase,
  Building,
  CreditCard,
  CheckCircle,
  Send,
  Loader2,
  Phone,
  Calendar
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocumentsRequired() {
  const [formData, setFormData] = useState({
    fullName: "",
    dob: "",
    nationality: "",
    maritalStatus: "",
    childrenDetails: "",
    currentAddress: "",
    currentAddressDate: "",
    previousAddresses: "",
    mobile: "",
    email: "",
    occupation: "",
    employerName: "",
    employerAddress: "",
    employerPhone: "",
    employmentLength: "",
    previousEmployment: "",
    propertyAddress: "",
    propertyPrice: "",
    propertyDetails: "",
    solicitorDetails: "",
    estateAgentDetails: "",
    loansCredits: "",
    adverseCredit: "",
    additionalNotes: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.mobile) {
      toast.error("Please fill in required fields (Name, Email, Phone)");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.fullName,
        email: formData.email,
        phone: formData.mobile,
        message: `MORTGAGE ENQUIRY FORM\n\n` +
          `Personal Details:\n` +
          `Full Name: ${formData.fullName}\n` +
          `DOB: ${formData.dob}\n` +
          `Nationality: ${formData.nationality}\n` +
          `Marital Status: ${formData.maritalStatus}\n` +
          `Children: ${formData.childrenDetails}\n` +
          `Current Address: ${formData.currentAddress} (Since: ${formData.currentAddressDate})\n` +
          `Previous Addresses: ${formData.previousAddresses}\n\n` +
          `Employment:\n` +
          `Occupation: ${formData.occupation}\n` +
          `Employer: ${formData.employerName}\n` +
          `Employer Address: ${formData.employerAddress}\n` +
          `Employer Phone: ${formData.employerPhone}\n` +
          `Length: ${formData.employmentLength}\n` +
          `Previous Employment: ${formData.previousEmployment}\n\n` +
          `Property:\n` +
          `Address: ${formData.propertyAddress}\n` +
          `Price: ${formData.propertyPrice}\n` +
          `Details: ${formData.propertyDetails}\n\n` +
          `3rd Parties:\n` +
          `Solicitor: ${formData.solicitorDetails}\n` +
          `Estate Agent: ${formData.estateAgentDetails}\n\n` +
          `Commitments:\n` +
          `Loans/Credits: ${formData.loansCredits}\n` +
          `Adverse Credit: ${formData.adverseCredit}\n\n` +
          `Additional Notes: ${formData.additionalNotes}`
      });
      toast.success("Form submitted successfully! We'll be in touch soon.");
      setFormData({
        fullName: "", dob: "", nationality: "", maritalStatus: "", childrenDetails: "",
        currentAddress: "", currentAddressDate: "", previousAddresses: "", mobile: "", email: "",
        occupation: "", employerName: "", employerAddress: "", employerPhone: "", employmentLength: "",
        previousEmployment: "", propertyAddress: "", propertyPrice: "", propertyDetails: "",
        solicitorDetails: "", estateAgentDetails: "", loansCredits: "", adverseCredit: "", additionalNotes: ""
      });
    } catch (error) {
      toast.error("Failed to submit. Please try again or call us directly.");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#020617] via-[#0f172a] to-[#020617] text-white">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-8">
        <Link to="/bio/zahirul" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Profile
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Documents Required</h1>
          <p className="text-slate-400">Complete this form or gather these documents for your mortgage application</p>
        </motion.div>

        {/* Initial Details Form */}
        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-6"
        >
          {/* Personal Information */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-blue-400" />
              </div>
              <h2 className="text-xl font-semibold">Personal Information</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name (including middle names) *</Label>
                <Input name="fullName" value={formData.fullName} onChange={handleChange} className="bg-slate-900 border-slate-700" required />
              </div>
              <div>
                <Label>Date of Birth *</Label>
                <Input name="dob" type="date" value={formData.dob} onChange={handleChange} className="bg-slate-900 border-slate-700" />
              </div>
              <div>
                <Label>Nationality</Label>
                <Input name="nationality" value={formData.nationality} onChange={handleChange} className="bg-slate-900 border-slate-700" />
              </div>
              <div>
                <Label>Marital Status</Label>
                <Input name="maritalStatus" value={formData.maritalStatus} onChange={handleChange} className="bg-slate-900 border-slate-700" placeholder="Single, Married, etc." />
              </div>
              <div className="md:col-span-2">
                <Label>Name & DOB of children under 18</Label>
                <Textarea name="childrenDetails" value={formData.childrenDetails} onChange={handleChange} className="bg-slate-900 border-slate-700" rows={2} />
              </div>
              <div className="md:col-span-2">
                <Label>Current Address</Label>
                <Input name="currentAddress" value={formData.currentAddress} onChange={handleChange} className="bg-slate-900 border-slate-700" />
              </div>
              <div>
                <Label>Date Moved In</Label>
                <Input name="currentAddressDate" type="date" value={formData.currentAddressDate} onChange={handleChange} className="bg-slate-900 border-slate-700" />
              </div>
              <div>
                <Label>Mobile *</Label>
                <Input name="mobile" type="tel" value={formData.mobile} onChange={handleChange} className="bg-slate-900 border-slate-700" required />
              </div>
              <div>
                <Label>Email *</Label>
                <Input name="email" type="email" value={formData.email} onChange={handleChange} className="bg-slate-900 border-slate-700" required />
              </div>
              <div className="md:col-span-2">
                <Label>Previous Addresses (3 year history with dates)</Label>
                <Textarea name="previousAddresses" value={formData.previousAddresses} onChange={handleChange} className="bg-slate-900 border-slate-700" rows={3} />
              </div>
            </div>
          </div>

          {/* Employment Details */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-purple-400" />
              </div>
              <h2 className="text-xl font-semibold">Employment Details</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Occupation</Label>
                <Input name="occupation" value={formData.occupation} onChange={handleChange} className="bg-slate-900 border-slate-700" />
              </div>
              <div>
                <Label>Employer Name</Label>
                <Input name="employerName" value={formData.employerName} onChange={handleChange} className="bg-slate-900 border-slate-700" />
              </div>
              <div className="md:col-span-2">
                <Label>Employer Address</Label>
                <Input name="employerAddress" value={formData.employerAddress} onChange={handleChange} className="bg-slate-900 border-slate-700" />
              </div>
              <div>
                <Label>Employer Phone</Label>
                <Input name="employerPhone" type="tel" value={formData.employerPhone} onChange={handleChange} className="bg-slate-900 border-slate-700" />
              </div>
              <div>
                <Label>Length of Employment</Label>
                <Input name="employmentLength" value={formData.employmentLength} onChange={handleChange} className="bg-slate-900 border-slate-700" placeholder="e.g., 2 years" />
              </div>
              <div className="md:col-span-2">
                <Label>If employed less than 1 year - previous employer details</Label>
                <Textarea name="previousEmployment" value={formData.previousEmployment} onChange={handleChange} className="bg-slate-900 border-slate-700" rows={2} />
              </div>
            </div>
          </div>

          {/* Property Details */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                <Building className="w-5 h-5 text-green-400" />
              </div>
              <h2 className="text-xl font-semibold">Purchase/Remortgage Property Details</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Property Address</Label>
                <Input name="propertyAddress" value={formData.propertyAddress} onChange={handleChange} className="bg-slate-900 border-slate-700" />
              </div>
              <div>
                <Label>Agreed/Market Price</Label>
                <Input name="propertyPrice" value={formData.propertyPrice} onChange={handleChange} className="bg-slate-900 border-slate-700" placeholder="£" />
              </div>
              <div className="md:col-span-2">
                <Label>Property Details (flat lease, service charge, rooms, etc.)</Label>
                <Textarea name="propertyDetails" value={formData.propertyDetails} onChange={handleChange} className="bg-slate-900 border-slate-700" rows={2} />
              </div>
            </div>
          </div>

          {/* 3rd Party Details */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-500/20 rounded-xl flex items-center justify-center">
                <User className="w-5 h-5 text-amber-400" />
              </div>
              <h2 className="text-xl font-semibold">3rd Party Details</h2>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Solicitor (name, address, email)</Label>
                <Textarea name="solicitorDetails" value={formData.solicitorDetails} onChange={handleChange} className="bg-slate-900 border-slate-700" rows={2} />
              </div>
              <div>
                <Label>Estate/Sales Agent (name, email)</Label>
                <Textarea name="estateAgentDetails" value={formData.estateAgentDetails} onChange={handleChange} className="bg-slate-900 border-slate-700" rows={2} />
              </div>
            </div>
          </div>

          {/* Commitments */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-red-400" />
              </div>
              <h2 className="text-xl font-semibold">Commitments/Adverse Credit</h2>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Loans/Credit Cards (providers, balances, monthly payments)</Label>
                <Textarea name="loansCredits" value={formData.loansCredits} onChange={handleChange} className="bg-slate-900 border-slate-700" rows={3} placeholder="Please indicate if any amounts are to be repaid before the mortgage starts" />
              </div>
              <div>
                <Label>Any adverse credit registered against your name?</Label>
                <Textarea name="adverseCredit" value={formData.adverseCredit} onChange={handleChange} className="bg-slate-900 border-slate-700" rows={2} placeholder="If yes, please give details" />
              </div>
              <div>
                <Label>Additional Notes</Label>
                <Textarea name="additionalNotes" value={formData.additionalNotes} onChange={handleChange} className="bg-slate-900 border-slate-700" rows={2} />
              </div>
            </div>
          </div>

          <Button type="submit" disabled={submitting} className="w-full h-14 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg">
            {submitting ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Submitting...</> : <><Send className="w-5 h-5 mr-2" /> Submit Details</>}
          </Button>
        </motion.form>

        {/* Documents Checklist */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur border border-blue-500/30 rounded-2xl p-6 mt-8"
        >
          <h2 className="text-xl font-bold mb-4">📄 Documents Checklist</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-medium text-blue-400 mb-2">📄 Proof of ID</h3>
              <ul className="text-slate-300 ml-4 space-y-1">
                <li>• Passport or Driving License</li>
                <li>• Visa documents (if applicable)</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-medium text-blue-400 mb-2">🏠 Proof of Address</h3>
              <ul className="text-slate-300 ml-4 space-y-1">
                <li>• Utility Bill (within last 3 months) OR</li>
                <li>• Latest Council Tax Bill</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-medium text-blue-400 mb-2">💼 Proof of Income</h3>
              <ul className="text-slate-300 ml-4 space-y-1">
                <li>• <strong>Employed:</strong> Latest 3 months payslips & P60</li>
                <li>• <strong>Self-employed:</strong> Last 2 years SA302 and Tax year overview</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-medium text-blue-400 mb-2">📝 Full Multi-agency Credit Report</h3>
              <p className="text-slate-300 ml-4">From <a href="https://www.checkmyfile.partners/253PGQ8/2CTPL/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">CheckMyFile</a></p>
            </div>
            
            <div>
              <h3 className="font-medium text-blue-400 mb-2">💳 Proof of Income & Expenditure</h3>
              <ul className="text-slate-300 ml-4 space-y-1">
                <li>• Latest 3 months bank statements</li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-medium text-blue-400 mb-2">💰 Proof of Deposit</h3>
              <ul className="text-slate-300 ml-4 space-y-1">
                <li>• Bank statements or savings statements</li>
                <li className="text-slate-400 italic">Gift money guidance provided during process</li>
              </ul>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6"
        >
          <a href="tel:07550199399" className="block">
            <Button className="w-full h-14 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-lg">
              <Phone className="w-5 h-5 mr-2" />
              Call Us Now
            </Button>
          </a>
          <Link to="/book-appointment" className="block">
            <Button className="w-full h-14 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg">
              <Calendar className="w-5 h-5 mr-2" />
              Book Consultation
            </Button>
          </Link>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center mt-8"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition text-sm">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-amber-500 rounded-lg flex items-center justify-center">
              <Home className="w-3 h-3 text-white" />
            </div>
            Thames Mortgage & Protection
          </Link>
          <p className="text-xs text-slate-500 mt-3">FCA Regulated • FRN 1045227</p>
          <p className="text-xs text-amber-500 mt-2 font-medium">Your home may be repossessed if you do not keep up repayments on your mortgage or loans secured on it.</p>
        </motion.div>
      </div>
    </div>
  );
}
